import { useQuery } from "@tanstack/react-query";
import { capabilityGapKeys } from "@/lib/query";

export type CapabilityStatus = "meets" | "below";

export interface CapabilityGapRow {
  id: string;
  name: string;
  kind: "competency" | "skill";
  categoryName: string | null;
  categoryColor: string | null;
  averageOrdinal: number;
  expectation: string | null;
  status: CapabilityStatus;
  cycleCount: number;
}

export interface PersonCapabilityGaps {
  personId: string;
  personName: string;
  roleId: string | null;
  roleName: string | null;
  roleLevelId: string | null;
  roleLevelName: string | null;
  meetsAtOrdinal: number;
  capabilities: CapabilityGapRow[];
  summary: { meets: number; below: number; rated: number; required: number };
}

/** Where one person stands against what their role requires — EPIC-023. */
export function usePersonCapabilityGaps(personId: string) {
  return useQuery({
    queryKey: capabilityGapKeys.forPerson(personId),
    queryFn: async (): Promise<PersonCapabilityGaps> => {
      const res = await fetch(`/api/persons/${personId}/capability-gaps`);
      if (!res.ok) {
        const json = (await res.json().catch(() => null)) as { error?: unknown } | null;
        throw new Error(
          typeof json?.error === "string" && json.error
            ? json.error
            : "Failed to load capability gaps",
        );
      }
      const json = (await res.json()) as { data: PersonCapabilityGaps };
      return json.data;
    },
  });
}

export interface TeamMemberCapability {
  personId: string;
  personName: string;
  roleName: string | null;
  roleLevelName: string | null;
  meets: number;
  below: number;
  rated: number;
  required: number;
}

export interface TeamCompetencyRollup {
  competencyId: string;
  competencyName: string;
  categoryName: string | null;
  categoryColor: string | null;
  ratedPeople: number;
  belowPeople: number;
  averageOrdinal: number;
}

export interface TeamCapabilityGaps {
  scopeLabel: string;
  meetsAtOrdinal: number;
  people: TeamMemberCapability[];
  competencies: TeamCompetencyRollup[];
}

/** The same comparison across a group — EPIC-023, WI-308. */
export function useTeamCapabilityGaps(roleId: string | null) {
  return useQuery({
    queryKey: capabilityGapKeys.forTeam(roleId),
    queryFn: async (): Promise<TeamCapabilityGaps> => {
      const query = roleId ? `?roleId=${encodeURIComponent(roleId)}` : "";
      const res = await fetch(`/api/capability-gaps/team${query}`);
      if (!res.ok) {
        const json = (await res.json().catch(() => null)) as { error?: unknown } | null;
        throw new Error(
          typeof json?.error === "string" && json.error
            ? json.error
            : "Failed to load team capabilities",
        );
      }
      const json = (await res.json()) as { data: TeamCapabilityGaps };
      return json.data;
    },
  });
}
